import React, { useEffect, useState } from 'react'
import { Card, Table, Button, Row, Col } from 'react-bootstrap'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const WaterRecord = ({
  data,
  startDate,
  endDate,
  setShowAddForm,
  onAddRecord,
}) => {
  // Process chart data
  const processChartData = () => {
    if (!startDate || !endDate || !Array.isArray(data)) {
      return {
        labels: [],
        datasets: [{ data: [] }],
      }
    }

    // Generate date range
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(currentDate.toISOString().split('T')[0])
      currentDate.setDate(currentDate.getDate() + 1)
    }

    const allDates = Array.from(dateSet).sort()

    // Calculate daily total water intake
    const dailyData = allDates.map((date) => {
      const dayRecords = data.filter((record) => {
        return (
          record && record.record_time && record.record_time.startsWith(date)
        )
      })

      if (dayRecords.length === 0) {
        return {
          date,
          record_time: `${date} 00:00:00`,
          totalAmount: 0,
        }
      }

      // Calculate daily total
      const totalAmount = dayRecords.reduce(
        (sum, record) => sum + (record.amount || 0),
        0
      )

      return {
        date,
        record_time: dayRecords[dayRecords.length - 1].record_time,
        totalAmount,
      }
    })

    // Format date for display
    const formatDateTime = (dateTimeStr) => {
      const date = new Date(dateTimeStr)
      const month = date.getMonth() + 1
      const day = date.getDate()
      return `${month}/${day}`
    }

    return {
      labels: allDates.map((date) => formatDateTime(`${date} 00:00:00`)),
      datasets: [
        {
          label: '每日喝水量',
          data: dailyData.map((item) => item.totalAmount),
          backgroundColor: 'rgba(53, 162, 235, 0.7)',
          borderColor: 'rgb(53, 162, 235)',
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `喝水量: ${context.raw} cc`
          },
        },
      },
    },
    scales: {
      y: {
        grid: {
          color: '#CCCCCC',
          borderDash: [2, 2],
        },
        ticks: {
          stepSize: 500,
        },
        title: {
          display: true,
          text: 'cc',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  }

  // Group data by date for table display
  const groupDataByDate = (records) => {
    if (!startDate || !endDate || !Array.isArray(records)) return []

    // Generate date range
    const dateSet = new Set()
    const start = new Date(startDate)
    const end = new Date(endDate)
    const currentDate = new Date(start)

    while (currentDate <= end) {
      dateSet.add(currentDate.toISOString().split('T')[0])
      currentDate.setDate(currentDate.getDate() + 1)
    }

    // Convert to array and sort (descending, newest first)
    let allDates = Array.from(dateSet).sort((a, b) => new Date(b) - new Date(a))

    // Create date to records mapping
    const recordsByDate = records.reduce((acc, record) => {
      if (!record || !record.record_time) return acc
      const date = record.record_time.split(' ')[0]
      if (!acc[date]) acc[date] = []
      acc[date].push(record)
      return acc
    }, {})

    // Create daily data structure
    return allDates.map((date) => {
      const dayRecords = recordsByDate[date] || []

      // Calculate daily total
      const dailyTotal = dayRecords.reduce(
        (sum, record) => sum + (record.amount || 0),
        0
      )

      // Sort records by time (newest first)
      const sortedRecords = dayRecords.sort(
        (a, b) => new Date(b.record_time) - new Date(a.record_time)
      )

      return {
        date,
        records: sortedRecords,
        dailyTotal,
      }
    })
  }

  // Format time
  const formatTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr)
    return `${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes()
    ).padStart(2, '0')}`
  }

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return `${dateStr} ${weekdays[date.getDay()]}`
  }

  const groupedData = groupDataByDate(data || [])

  return (
    <Row className="water-record g-3">
      {/* Chart Section */}
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <Card.Title className="d-flex justify-content-end align-items-center mb-4">
              <div className="ms-3 d-flex">
                <span>
                  <i
                    className="fas fa-square me-1"
                    style={{ color: 'rgba(53, 162, 235, 0.7)' }}
                  ></i>
                  每日喝水量
                </span>
              </div>
            </Card.Title>
            <div className="responsive-chart">
              <Bar options={chartOptions} data={processChartData()} />
            </div>
          </Card.Body>
        </Card>
      </Col>

      {/* Table Section */}
      <Col xs={12} lg={6}>
        <Card className="responsive-card">
          <Card.Body>
            <div className="table-responsive">
              {groupedData.map((dayGroup) => (
                <Table
                  key={dayGroup.date}
                  hover
                  className="mb-0 border rounded-3 overflow-hidden"
                >
                  <thead>
                    <tr className="table-light">
                      <th colSpan="2">{formatDate(dayGroup.date)}</th>
                      <th className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="px-3"
                          onClick={() => {
                            setShowAddForm(true)
                            onAddRecord?.(dayGroup.date)
                          }}
                        >
                          <i className="fa-solid fa-plus me-2"></i>
                          新增
                        </Button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayGroup.records.length > 0 ? (
                      <>
                        {dayGroup.records.map((record, index) => (
                          <tr key={record.action_id || index}>
                            <td>{formatTime(record.record_time)}</td>
                            <td>{record.amount} cc</td>
                            <td>{record.comment || '-'}</td>
                          </tr>
                        ))}
                        <tr className="table-light">
                          <td colSpan="3" className="text-end">
                            總喝水量：{dayGroup.dailyTotal} cc
                          </td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted">
                          尚無喝水紀錄
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default WaterRecord
